import { Axis, Chart, Coord, Geom, Legend, Tooltip } from "bizcharts";
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Col, Grid, Row, Tab, Tabs, Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withProps, withStateHandlers, withHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { FiInfo } from 'react-icons/fi';
import { adminActions } from '../../../../state/modules/admin';
import { industryActions } from '../../../../state/modules/industry';
import { pluralise } from "../../../../utils";
import { LoadingButton, Icon, DateSelect } from '../../../components';
import { default as CaddieTooltip } from '../../../components/tooltip';
import Loading from "../../loading";
import { downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream, currentYear } from '../../../../utils';
import { format } from 'utils/date-fns-compat';

const uuidv1 = require('uuid/v1');

const TaskStatus = {
  created: 'created',
  waiting: 'waiting',
  finished: 'finished'
}

const userInfoColumns = [
  'User Id',
  'First Name',
  'Last Name',
  'Email',
  'Business Name',
  'User Type',
  'User status',
  'CPD End Date',
  'Required CPD Points' 
]

const CpdAvailability = ({
  allCpdCategories,
  totalUserCount,
  estimationTasks,
  startEstimation,
  estimations,
  isEstimatingTasks,
  estimationProgress,
  industriesWithCpdCategorys,
  exportCsv,
  reportDate,
  onDateRangeChange
}) => {
  const data = fp.flow(
    fp.flatMap('availability'),
    fp.map(
      ({ cpdCategoryId, points, cpdCategoryName }) => {
        return {
          cpdCategoryId,
          availablePoints: points,
          cpdCategoryName: he.decode(cpdCategoryName)
        }
      }
    ),
    fp.groupBy(
      (v) => JSON.stringify(v)
    ),
    fp.toPairs,
    fp.map(
      ([key, value]) => {
        const parsed = JSON.parse(key)
        const size = value.length
        const radius = Math.sqrt(size) + 5
        return {
          ...parsed,
          size,
          radius
        }
      }
    ),
    fp.sortBy(
      ({ cpdCategoryName }) => fp.lowerCase(cpdCategoryName)
    )
  )(estimations);

  /**
   * The gap between the points available in caddie and the user required
   * to satisfy his training goal.
   * Used to tell caddie admin how many points they need to put into caddie library.
   */
  const gapData = fp.flow(
    fp.flatMap('availability'),
    fp.map(
      ({ cpdCategoryId, gap, cpdCategoryName }) => {
        return {
          cpdCategoryId,
          gapPoints: gap,
          cpdCategoryName: he.decode(cpdCategoryName)
        }
      }
    ),
    fp.groupBy(
      (v) => JSON.stringify(v)
    ),
    fp.toPairs,
    fp.map(
      ([key, value]) => {
        const parsed = JSON.parse(key)
        const size = value.length
        const radius = Math.sqrt(size) + 5
        return {
          ...parsed,
          size,
          radius
        }
      }
    ),
    fp.sortBy(
      ({ cpdCategoryName }) => fp.lowerCase(cpdCategoryName)
    )
  )(estimations);

  return <div className='base-layout'>
    <section className='layout--header'>
      <Grid>
        <Row className='button-gutter'>
          <Col xs={12}>
            <h1>CPD Availability</h1>
          </Col>
        </Row>
      </Grid>
    </section>
    <section id='section--admin-content' className='layout--section'>
      <Grid>
        <Row className='no-gutter sectionHeader'>
          <Col xs={12} md={5}>
            <form>
              <table className='table--date-search'>
                <tbody>
                  <tr>
                    <td>
                      Generate report for
                    </td>
                    <td>
                      <DateSelect
                        date={reportDate}
                        onChange={(date) => onDateRangeChange(date)}
                        disabled={isEstimatingTasks}
                        emptyOption={false}
                        minYear={currentYear()}
                        futureYears={1}
                      />
                    </td>
                    <td>
                      <CaddieTooltip
                        placement='bottom'
                        component={
                          <FiInfo className='icon-green' size={20}></FiInfo>
                        }
                        tip={
                          <p>
                            If the future date you've selected is <strong>after</strong> the users CPD year end date,
                            then the data will not adjust the gap hours for the users' new CPD year.
                            It will only show the gap hours for the current CPD year until their current CPD year end date has passed.
                          </p>
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          </Col>
        </Row>
      </Grid>
    </section>
    <section id='section--admin-cpd-availability' className='layout--section'>
      <Grid>
        <Row className='no-gutter sectionHeader'>
          <Col xs={12} md={5}>
            {
              isEstimatingTasks ? `Estimation Progress (${estimationProgress})` : null
            }
            <LoadingButton loading={isEstimatingTasks} onClick={startEstimation} text={`Estimate CPD Availability for ${totalUserCount} Users`} />
          </Col>
          <Col xs={12} md={2} mdOffset={5}>
            <Button
              className='large full pull-down'
              onClick={exportCsv}>
              <Icon iconSet='glyphicon' icon='download-alt' className="space-right" /> export to csv
            </Button>
          </Col>
        </Row>

        {
          (data.length > 0) && (!isEstimatingTasks) ?
            <Row className='no-gutter top-gap'>
              <Col>
                <Tabs id='estimation-tabs'>
                  {
                    fp.flow(
                      fp.sortBy(
                        ({ cpdCategorys }) => -1 * cpdCategorys.length
                      ),
                      fp.map(
                        ({ industry, cpdCategorys }) => {
                          const sortedCpdCategorys = fp.sortBy(
                            ({ name }) => fp.lowerCase(name)
                          )(cpdCategorys);

                          const industryData = fp.flow(
                            fp.filter(
                              ({ cpdCategoryId }) => fp.flow(
                                fp.map('id'),
                                fp.includes(cpdCategoryId)
                              )(cpdCategorys)
                            ),
                            fp.map(
                              (cpdCategory) => {
                                const sortedCpdCategoryIds = fp.map('id')(sortedCpdCategorys)
                                return {
                                  ...cpdCategory,
                                  cpdCategoryIndex: -1 * fp.indexOf(cpdCategory.cpdCategoryId)(sortedCpdCategoryIds)
                                }
                              }
                            )
                          )(data);

                          const gapIndustryData = fp.flow(
                            fp.filter(
                              ({ cpdCategoryId }) => fp.flow(
                                fp.map('id'),
                                fp.includes(cpdCategoryId)
                              )(cpdCategorys)
                            ),
                            fp.map(
                              (cpdCategory) => {
                                const sortedCpdCategoryIds = fp.map('id')(sortedCpdCategorys)
                                return {
                                  ...cpdCategory,
                                  cpdCategoryIndex: -1 * fp.indexOf(cpdCategory.cpdCategoryId)(sortedCpdCategoryIds)
                                }
                              }
                            )
                          )(gapData);

                          const minData = fp.flow(
                            fp.sortBy(
                              ({ name }) => fp.lowerCase(name)
                            ),
                            fp.map(
                              (cpdCategory) => {
                                const dataForCpdCategory = fp.filter(
                                  ({ cpdCategoryId }) => cpdCategoryId === cpdCategory.id
                                )(industryData)
                                return fp.minBy('availablePoints')(dataForCpdCategory)
                              }
                            ),
                            fp.reverse
                          )(cpdCategorys);

                          const gapMaxData = fp.flow(
                            fp.sortBy(
                              ({ name }) => fp.lowerCase(name)
                            ),
                            fp.map(
                              (cpdCategory) => {
                                const dataForCpdCategory = fp.filter(
                                  ({ cpdCategoryId }) => cpdCategoryId === cpdCategory.id
                                )(gapIndustryData)
                                return fp.maxBy('gapPoints')(dataForCpdCategory)
                              }
                            ),
                            fp.reverse
                          )(cpdCategorys);

                          return <Tab key={industry.id} eventKey={industry.name} title={he.decode(industry.name)}>
                            <Chart height={Math.max(cpdCategorys.length * 30, 200)} data={minData} forceFit padding={{ left: 250, top: 50, bottom: 30, right: 30 }}>
                              <Coord transpose />
                              <Axis
                                name="cpdCategoryName"
                                label={{
                                  offset: 12
                                }}
                              />
                              <Axis name="availablePoints" />
                              <Tooltip />
                              <Geom type="interval" position="cpdCategoryName*availablePoints" />
                            </Chart>

                            <Chart height={300 + Math.max(cpdCategorys.length * 25, 200)} data={industryData} forceFit padding={{ top: 50, bottom: 30, right: 30, left: 250 }}>
                              <Tooltip
                                showTitle={false}
                                crosshairs={{
                                  type: "cross"
                                }}
                                itemTpl="<li data-index={index} style=&quot;margin-bottom:4px;&quot;><span style=&quot;background-color:{color};&quot; class=&quot;g2-tooltip-marker&quot;></span>{name}<br/>{value}</li>"
                              />
                              <Axis name="availablePoints" />
                              <Axis label={{
                                formatter: (text, item, index) => {
                                  return ''
                                }
                              }} name="cpdCategoryIndex" />
                              <Legend position='left' sizeType='null' />
                              <Geom
                                type="point"
                                position="availablePoints*cpdCategoryIndex"
                                color="cpdCategoryName"
                                opacity={0.65}
                                shape="circle"
                                size={['radius', [5, 10]]}
                                tooltip={[
                                  "cpdCategoryName*availablePoints*cpdCategoryIndex*size",
                                  (cpdCategoryName, availablePoints, cpdCategoryIndex, size) => {
                                    return {
                                      name: cpdCategoryName,
                                      value: `${availablePoints} points available in ${cpdCategoryName} for ${size} ${pluralise('user', size)}`
                                    };
                                  }
                                ]}
                              />
                            </Chart>
                            <hr />
                            <h3>CPD Availability Gap</h3>
                            <Chart height={Math.max(cpdCategorys.length * 30, 200)} data={gapMaxData} forceFit padding={{ left: 250, top: 50, bottom: 30, right: 30 }}>
                              <Coord transpose />
                              <Axis
                                name="cpdCategoryName"
                                label={{
                                  offset: 12
                                }}
                              />
                              <Axis name="gapPoints" />
                              <Tooltip />
                              <Geom type="interval" position="cpdCategoryName*gapPoints" />
                            </Chart>

                            <Chart height={300 + Math.max(cpdCategorys.length * 25, 200)} data={gapIndustryData} forceFit padding={{ top: 50, bottom: 30, right: 30, left: 250 }}>
                              <Tooltip
                                showTitle={false}
                                crosshairs={{
                                  type: "cross"
                                }}
                                itemTpl="<li data-index={index} style=&quot;margin-bottom:4px;&quot;><span style=&quot;background-color:{color};&quot; class=&quot;g2-tooltip-marker&quot;></span>{name}<br/>{value}</li>"
                              />
                              <Axis name="gapPoints" />
                              <Axis label={{
                                formatter: (text, item, index) => {
                                  return ''
                                }
                              }} name="cpdCategoryIndex" />
                              <Legend position='left' sizeType='null' />
                              <Geom
                                type="point"
                                position="gapPoints*cpdCategoryIndex"
                                color="cpdCategoryName"
                                opacity={0.65}
                                shape="circle"
                                size={['radius', [5, 10]]}
                                tooltip={[
                                  "cpdCategoryName*gapPoints*cpdCategoryIndex*size",
                                  (cpdCategoryName, gapPoints, cpdCategoryIndex, size) => {
                                    return {
                                      name: cpdCategoryName,
                                      value: `${gapPoints} points required in ${cpdCategoryName} for ${size} ${pluralise('user', size)}`
                                    };
                                  }
                                ]}
                              />
                            </Chart>
                          </Tab>
                        }
                      )
                    )(industriesWithCpdCategorys)
                  }
                </Tabs>
              </Col>
            </Row> : null
        }
      </Grid>
    </section>
  </div>
}

const mapStateToProps = (state) => ({
  userIds: state.admin.userIds,
  isEstimating: state.admin.isEstimatingCpdAvailability,
  estimations: state.admin.cpdAvailabilityEstimations,
  allCpdCategories: state.cpdCategorys.cpdCategorys,
  industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys
})
const mapDispatchToProps = (dispatch) => ({
  getAllUserIds: bindActionCreators(adminActions.admin.getAllUserIds, dispatch),
  estimate: bindActionCreators(adminActions.admin.estimateCpdAvailability, dispatch),
  getIndustriesWithCpdCategorys: bindActionCreators(industryActions.industries.industriesWithCpdCategorys, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ userIds }) => ({
      totalUserCount: userIds.length
    })
  ),
  withStateHandlers(
    {
      estimationTasks: [],
      reportDate: format(new Date()),
    },
    {
      startEstimation: (state, { userIds }) => () => ({
        estimationTasks: fp.flow(
          fp.chunk(5),
          fp.map(
            (ids) => {
              return {
                id: uuidv1(),
                status: TaskStatus.created,
                params: ids
              }
            }
          )
        )(userIds)
      }),
      runTask: ({ estimationTasks }) => (taskToRun) => ({
        estimationTasks: fp.map(
          (task) => task.id === taskToRun.id ? {
            ...task,
            status: TaskStatus.waiting
          } : task
        )(estimationTasks)
      }),
      finishWaitingTasks: ({ estimationTasks }) => () => ({
        estimationTasks: fp.map(
          (task) => task.status === TaskStatus.waiting ? {
            ...task,
            status: TaskStatus.finished
          } : task
        )(estimationTasks)
      }),
      onDateRangeChange: () => (date) => ({
        reportDate: format(date),
      })
    }
  ),
  withProps(
    ({ estimationTasks }) => ({
      createdTasks: fp.filter(
        ({ status }) => status === TaskStatus.created
      )(estimationTasks),
      waitingTasks: fp.filter(
        ({ status }) => status === TaskStatus.waiting
      )(estimationTasks),
      finishedTasks: fp.filter(
        ({ status }) => status === TaskStatus.finished
      )(estimationTasks),
      isEstimatingTasks: !fp.all(
        ({ status }) => status === TaskStatus.finished
      )(estimationTasks) && estimationTasks.length > 0
    })
  ),
  withProps(
    ({ estimationTasks, finishedTasks }) => ({
      estimationProgress: `${finishedTasks.length}/${estimationTasks.length}`
    })
  ),
  withHandlers({
    exportCsv: ({ estimations, industriesWithCpdCategorys }) => () => {      
      if (estimations.length > 0) {
        const categoryColumns = fp.flow(
          fp.sortBy((categoryEstimation) => categoryEstimation.cpdCategoryName),
          fp.map((categoryEstimation) => {
            const categoryName = categoryEstimation.cpdCategoryName;
            const categoryIndustry = industriesWithCpdCategorys.filter((industryWithCategories) => {
              const categoryIds = industryWithCategories.cpdCategorys.map((category) => category.id);
              return categoryIds.includes(categoryEstimation.cpdCategoryId);
            });
            const industryName = categoryIndustry.length === 0 ? 'No Industry' : categoryIndustry[0].industry.name;
            const columnName = `${categoryName} (${industryName})`;
            return columnName;
          }),
        )(estimations[0].availability)

        const columns = [...userInfoColumns, ...categoryColumns]

        const rows = estimations.map((estimation) => {
          const user = estimation.user;
          const userStatus = user.activated === true ? 'Active' : "Inactive";
          const businessName = estimation.businesses.length === 0 ? '' : estimation.businesses[0].name;
          const userType = estimation.roleAssignments.map((assignment) => assignment.role).join(', ');
          const userData = [user.id, user.firstName, user.lastName, user.email, businessName, userType, userStatus, estimation.cpdEndDate, estimation.requiredCPDPoints];

          const categoryGapData = fp.flow(
            fp.sortBy((categoryEstimation) => categoryEstimation.cpdCategoryName),
            fp.map((categoryEstimation) => categoryEstimation.gap),
          )(estimation.availability)

          return [...userData, ...categoryGapData]
        })

        downloadBlobStreamFile(writeDataToCsvStream(getCsvStream({
          header: true,
          columns
        }), rows).pipe(getBlobStream()), 'gap-points-report.csv')
      }
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        getAllUserIds,
        getIndustriesWithCpdCategorys
      } = this.props

      getAllUserIds()
      getIndustriesWithCpdCategorys()
    },
    componentDidUpdate(prevProps) {
      const {
        createdTasks,
        waitingTasks,
        runTask,
        isEstimating,
        finishWaitingTasks,
        estimate,
        reportDate
      } = this.props
      if (fp.isEmpty(waitingTasks)) {
        const pendingTask = fp.first(createdTasks)
        if (pendingTask) {
          runTask(pendingTask)
          estimate(pendingTask.params, reportDate)
        }
      }
      if (!isEstimating && prevProps.isEstimating) {
        finishWaitingTasks()
      }
    }
  }),
  branch(
    ({ industriesWithCpdCategorys }) => fp.isEmpty(industriesWithCpdCategorys),
    renderComponent(Loading)
  )
)(CpdAvailability)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/cpd-availability-portal/cpd-availability.js